import { sourceFilterDisplayName, OTHER_SOURCE_SYSTEMS } from "@/components/reviews";
import type { ReviewFilterParams, ReviewSnapshotParams } from "@/services/bedrock";
import { parseSnapshotPeriodDate, type SnapshotPeriod } from "./Overview/snapshotHelpers";
import type { XlsxReportMeta } from "@/utils/xlsxExportHelpers";
import { endOfWeek, format } from "date-fns";

export function buildReputationReportMeta({
  reportName,
  description,
  companyName,
  filters,
  totalRecords,
}: {
  reportName: string;
  description?: string;
  companyName?: string | null;
  filters?: string[];
  totalRecords?: number;
}): XlsxReportMeta {
  return {
    reportName,
    description,
    companyName: companyName || undefined,
    generatedAt: new Date(),
    filters: (filters ?? []).filter(Boolean),
    totalRecords,
  };
}

export function formatOverviewPeriodFilter(period: SnapshotPeriod, periodDate?: string | null): string {
  if (period === "all_time") return "Period: All time";
  if (!periodDate) return `Period: ${period === "weekly" ? "Weekly" : "Monthly"}`;
  const date = parseSnapshotPeriodDate(periodDate);
  if (period === "monthly") return `Period: ${format(date, "MMM yyyy")}`;
  const end = endOfWeek(date, { weekStartsOn: 1 });
  return `Period: ${format(date, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
}

export function formatOverviewScopeFilter({
  playerLabel,
  segmentationLabel,
}: {
  playerLabel?: string | null;
  segmentationLabel?: string | null;
}): string {
  if (playerLabel) return `Scope: Player — ${playerLabel}`;
  if (segmentationLabel) return `Scope: Segment — ${segmentationLabel}`;
  return "Scope: Entire Company";
}

export function formatPlayersExportFilters(params: ReviewSnapshotParams & { scopeLabel?: string }): string[] {
  return [
    formatOverviewPeriodFilter((params.periodType as SnapshotPeriod) ?? "all_time", params.date),
    params.scopeLabel ?? (params.withinSegmentationDetailId != null ? "Scope: Segment" : "Scope: Entire Company"),
  ].filter(Boolean);
}

export function formatReviewExportFilters(
  params: ReviewFilterParams,
  extras?: { playerLabel?: string | null }
): string[] {
  const filters: string[] = [];

  if (params.startDate || params.endDate) {
    const start = params.startDate ? format(new Date(`${params.startDate}T00:00:00`), "MMM d, yyyy") : "…";
    const end = params.endDate ? format(new Date(`${params.endDate}T00:00:00`), "MMM d, yyyy") : "…";
    filters.push(`Date range: ${start} – ${end}`);
  } else {
    filters.push("Date range: All time");
  }

  if (extras?.playerLabel) filters.push(`Player: ${extras.playerLabel}`);
  else if (params.playerId != null) filters.push(`Player ID: ${params.playerId}`);

  if (params.segmentationIds?.length) {
    filters.push(`Segments: ${params.segmentationIds.join(", ")}`);
  }

  if (params.search) filters.push(`Search: ${params.search}`);

  if (params.sourceSystem) {
    if (Array.isArray(params.sourceSystem)) {
      const systems = params.sourceSystem;
      const isOther =
        systems.length === OTHER_SOURCE_SYSTEMS.length && OTHER_SOURCE_SYSTEMS.every(code => systems.includes(code));
      const sourceLabel = isOther ? "Other" : systems.map(code => sourceFilterDisplayName(code) ?? code).join(", ");
      filters.push(`Source: ${sourceLabel}`);
    } else {
      filters.push(`Source: ${sourceFilterDisplayName(params.sourceSystem) ?? params.sourceSystem}`);
    }
  }

  if (params.sentiment) {
    const sentiment = Array.isArray(params.sentiment) ? params.sentiment.join(", ") : params.sentiment;
    filters.push(`Sentiment: ${sentiment}`);
  }

  if (params.ratingType?.length) filters.push(`Rating type: ${params.ratingType.join(", ")}`);
  if (params.starRating != null) filters.push(`Star rating: ${params.starRating}`);
  if (params.npsScore != null) filters.push(`NPS score: ${params.npsScore}`);
  if (params.tenPointRating != null) filters.push(`10-point rating: ${params.tenPointRating}`);
  if (params.npsCategory) filters.push(`NPS category: ${params.npsCategory}`);
  if (params.sourceSentiment) filters.push(`Recommendation: ${params.sourceSentiment}`);
  else if (params.hasSourceSentiment) filters.push("Rating type: Recommendation");

  if (params.needingReply) filters.push("Reply status: Needs reply");
  else if (params.replied) filters.push("Reply status: Replied");
  else if (params.replySkipped) filters.push("Reply status: No reply needed");

  return filters;
}
